/**
 * Diğer: the section pages (Satış, Mağaza, Sistem) with a one-line intro each, the settings links, the theme choice
 * and the account. A page with longer help keeps it behind its own "Nasıl çalışır" (audit 25 Oct 2026, F18).
 */

import { createContext, useContext } from 'react';
import { useAuth } from '../app/auth-context.js';
import { usePanel, useStore } from '../app/context.js';
import { useIsDesktop } from '../app/media.js';
import { Link, type SectionId } from '../app/router.js';
import { outboxWaiting } from '../app/selectors.js';
import { setupSummary } from '../app/settings.js';
import { setThemePreference, THEME_LABELS, type ThemePreference, useThemePreference } from '../app/theme.js';
import { ChannelsPage } from '../components/channels.js';
import { CustomersPage } from '../components/customers.js';
import { Icon, type IconName } from '../components/icons.js';
import { KbPage } from '../components/kb.js';
import { LogPage } from '../components/log.js';
import { OutboxPage } from '../components/outbox.js';
import { OutreachPage } from '../components/outreach.js';
import { OutreachLogPage } from '../components/outreach-log.js';
import { OzetPage } from '../components/ozet.js';
import { SectionHeader } from '../components/primitives.js';
import { ProductsPage } from '../components/products.js';
import { ProspectsPage } from '../components/prospects.js';
import { ReplyExamplesPage } from '../components/reply-examples.js';
import { SalesMapPage } from '../components/sales-map.js';
import { SekreterPage } from '../components/sekreter.js';
import { SeoPage } from '../components/seo-report.js';
import { AppBar, BottomTabBar, StatusArea, TopBar, TopBarStatus } from '../components/shell.js';
import { ListingsPage, SalesPage } from '../components/shop.js';
import { SiteChatsPage } from '../components/site-chats.js';
import { SitePage } from '../components/site-stats.js';
import { VoicePage } from '../components/voice.js';
import { DoneRow } from '../components/widgets.js';
import { formatClock, formatDayMonth, formatLongDate } from '../lib/format.js';
import { Loading, Segmented } from './common.js';

/** Mock-only controls (offline simulation). Absent when a real API is wired in. */
export interface DevTools {
  setOnline: (online: boolean) => void;
  online: () => boolean;
}

export const DevToolsContext = createContext<DevTools | null>(null);

type SectionGroup = 'Satış' | 'Mağaza' | 'Sistem';

const SECTION_META: Record<SectionId, { title: string; icon: IconName; group: SectionGroup; text: string }> = {
  ozet: {
    title: 'Özet',
    icon: 'layout-grid',
    group: 'Mağaza',
    text: 'Senden bekleyenler, sonra seçtiğin dönem (bugün, bu hafta, bu ay, son 30 gün) bir öncekiyle: satış, site ve kaynakları, arama, yapay zekâ, müşteri mesajları, tanıtım mailleri, sosyal medya, ekip ve maliyet; gün gün ve hafta hafta. Ölçülmeyen yazılır, 0 gösterilmez.',
  },
  leadler: {
    title: 'Leadler',
    icon: 'map-pin',
    group: 'Satış',
    text: 'Tarama haritası, lead hunisi ve öğrenme döngüsü.',
  },
  'potansiyel-musteriler': {
    title: 'Potansiyel müşteriler',
    icon: 'user-search',
    group: 'Satış',
    text: "Bulunan her firma: hangi gün hangi aramadan geldi, ne iş yapıyor, üretim yapıyor mu, neden potansiyel, neden doğrulandı ya da doğrulanmadı, kime mail gitti. Canlı; Excel'e aktarılır.",
  },
  'tanitim-gonderimi': {
    title: 'Tanıtım gönderimi',
    icon: 'mail',
    group: 'Satış',
    text: 'Tanıtım mailleri gitmeden önce: her kampanyanın şartları (gönderim kutusu, gelen kutusu testi, kalite denetimi, kampanya onayı, adresler) ve ilk dalganın mailleri, Türkçesi üstte, gidecek İngilizcesi altta. Buradan hiçbir şey gitmez.',
  },
  'tanitim-takibi': {
    title: 'Tanıtım takibi',
    icon: 'mail-check',
    group: 'Satış',
    text: 'Hangi firmaya hangi mail ne zaman gitti, kim cevap verdi; adresi doğrulanıp sıraya giren firmalar ve her birinin neyi beklediği; cevap ve geri dönme oranları. Buradan hiçbir şey gitmez.',
  },
  satislar: {
    title: 'Satışlar',
    icon: 'receipt',
    group: 'Mağaza',
    text: 'Son 12 ay, ülke kırılımı ve siparişler; kaynak Etsy okuması (07:00-22:00 arası üç saatte bir).',
  },
  'satis-haritasi': {
    title: 'Satış haritası',
    icon: 'globe',
    group: 'Mağaza',
    text: 'Etsy siparişleri ülke ve şehir bazında harita üzerinde: dönem ve ürün türü seçilir, ülkeler gelire göre koyulaşır, şehirler sipariş sayısına göre büyür. Alıcı adı tutulmaz, en ince ayrıntı şehirdir.',
  },
  ilanlar: {
    title: 'İlanlar',
    icon: 'tag',
    group: 'Mağaza',
    text: 'İlanlar: Etsy ziyareti, görüntülenme, favori, 90 günlük sipariş ve ciro; aylık geçmiş ve arama terimleri.',
  },
  urunler: {
    title: 'Ürünler',
    icon: 'package',
    group: 'Mağaza',
    text: 'Ürün kartları ve render galerileri: her dosyanın neyi kapsadığı, ölçüleri, biçimleri ve Etsy ilanı.',
  },
  musteriler: {
    title: 'Müşteriler',
    icon: 'user-round',
    group: 'Mağaza',
    text: 'Kim yazdı, kim aldı: kaynağa göre konuşma, cevap ve satışa dönüş; müşteri bazında zaman çizgisi.',
  },
  site: {
    title: 'Site',
    icon: 'activity',
    group: 'Mağaza',
    text: 'cadgaragepro.com son 30 gün: kaç kişi geldi, nereden geldi (Google, yapay zekâ, Etsy), hangi botlar okudu, hangi sayfalar açıldı, Etsy düğmesi, Google ve Bing aramaları. Her pazartesi aynı rakamlar Telegram’a gelir.',
  },
  'site-sohbetleri': {
    title: 'Site sohbetleri',
    icon: 'message-square',
    group: 'Mağaza',
    text: 'Ziyaretçilerin cadgaragepro.com asistanıyla yazışmaları, Türkçesiyle; asistan "Ekibe gönder" önerdi mi. Her sohbetin ilk mesajı Telegram’a da gelir.',
  },
  kanallar: {
    title: 'Kanallar',
    icon: 'share',
    group: 'Mağaza',
    text: 'Etsy, site, YouTube, Pinterest ve CGTrader tek sayfada; üstte aylık gerçek gelir ile 1 yıllık tahmin. Rakamlar her sabah okunur, pazartesi özeti Telegram’a gelir.',
  },
  seslendirme: {
    title: 'Seslendirme',
    icon: 'mic',
    group: 'Mağaza',
    text: 'Video anlatımı için metni sese çevirir: metni yapıştır, sesi seç, her parça ayrı mp3 olur. Beş dakika içinde başlar; dinle ya da indir.',
  },
  seo: {
    title: 'SEO ve yapay zekâ',
    icon: 'search',
    group: 'Mağaza',
    text: 'Hangi sorular ve kelimeler test ediliyor, neredeyiz: her pazartesi 20 alıcı sorusu yapay zekâ cevabına ve Brave aramasına sorulur (anıldık mı, kaçıncıyız, yerimizi kim aldı); Google ve Bing’de bizi bulduran kelimeler, sayfalar ve ülkeler son 28 gün, önceki 28 günle.',
  },
  yapilanlar: {
    title: 'Yapılanlar',
    icon: 'list-checks',
    group: 'Sistem',
    text: 'Yürütücünün uyguladıkları ve senin gönderdiklerin.',
  },
  'bilgi-bankasi': {
    title: 'Bilgi bankası',
    icon: 'book-open',
    group: 'Sistem',
    text: 'Ürün kartlarından üretilen cevaplar; taslakları burada onaylarsın.',
  },
  'gecmis-cevaplar': {
    title: 'Geçmiş cevaplar',
    icon: 'file-text',
    group: 'Sistem',
    text: 'Gönderilmiş kutusundan alınan eski cevapların; Temsilci benzer soruda örnek alır, yanlışını burada emekliye ayırırsın.',
  },
  gonderilecekler: {
    title: 'Gönderilecekler',
    icon: 'send',
    group: 'Sistem',
    text: "Onayladığın e-posta cevapları: yazışmayı ve iki dildeki metni son kez oku, Gönder de; Gmail'den gider.",
  },
  sekreter: {
    title: 'Sekreter',
    icon: 'inbox',
    group: 'Sistem',
    text: 'Senden bekleyenlerin Telegram listesi ve kendi maddelerin; yapınca burada kapatırsın.',
  },
  kayitlar: {
    title: 'Kayıtlar',
    icon: 'file-text',
    group: 'Sistem',
    text: 'Salt okunur olay akışı: onaylar, görevler, bağlantılar, girişler ve sistem işleri. Yeni olaylar kendiliğinden eklenmez; “yeni olay” düğmesiyle gelir.',
  },
};

const ORDER: SectionId[] = [
  'leadler',
  'potansiyel-musteriler',
  'tanitim-gonderimi',
  'tanitim-takibi',
  'ozet',
  'satislar',
  'satis-haritasi',
  'ilanlar',
  'urunler',
  'musteriler',
  'site',
  'site-sohbetleri',
  'kanallar',
  'seslendirme',
  'seo',
  'yapilanlar',
  'bilgi-bankasi',
  'gecmis-cevaplar',
  'gonderilecekler',
  'sekreter',
  'kayitlar',
];

/** Politika and Ayarlar open the settings screens; the rest are the section pages. */
function SettingsRows() {
  const { settings } = usePanel();
  const setup = settings ? setupSummary(settings) : null;
  return (
    <>
      <Link to="/ayarlar/politika" className="more-row">
        <Icon name="shield" size={18} />
        <span className="more-row__title">Politika</span>
        <Icon name="chevron-right" size={18} className="ink-3" />
      </Link>
      <Link
        to="/ayarlar"
        className="more-row"
        aria-label={setup ? `Ayarlar, kurulum ${setup.ready} / ${setup.total} hazır` : undefined}
      >
        <Icon name="sliders" size={18} />
        <span className="more-row__title">Ayarlar</span>
        {setup && setup.ready < setup.total ? (
          <span className="mono more-row__count">
            {setup.ready}/{setup.total}
          </span>
        ) : null}
        <Icon name="chevron-right" size={18} className="ink-3" />
      </Link>
    </>
  );
}

function ThemeChooser() {
  const preference = useThemePreference();
  const options = (['system', 'light', 'dark'] as const).map((id) => ({ id, label: THEME_LABELS[id], count: null }));
  return (
    <div className="setting">
      <span className="setting__label">Tema</span>
      <Segmented<ThemePreference>
        label="Tema"
        controls="tema"
        value={preference}
        onChange={(value) => setThemePreference(value)}
        options={options}
      />
    </div>
  );
}

function OfflineToggle() {
  const dev = useContext(DevToolsContext);
  const { connection } = usePanel();
  if (!dev) return null;
  const online = connection !== 'stale';
  return (
    <div className="setting">
      <span className="setting__label">
        Deneme verisi
        <span className="setting__hint">Bağlantı kesilince onay düğmeleri kapanır.</span>
      </span>
      <button type="button" className="btn btn--secondary" onClick={() => dev.setOnline(!online)}>
        {online ? 'Bağlantıyı kes' : 'Bağlantıyı geri getir'}
      </button>
    </div>
  );
}

/** Who is signed in and how; logout for a session of the panel's own login (ADR 0012). */
function AccountSection() {
  const auth = useAuth();
  if (auth === null) return null;
  const { session } = auth;
  const expires = session.expiresAt === null ? null : Date.parse(session.expiresAt);
  const hint =
    session.via === 'session'
      ? `Panel girişi (${session.username ?? 'kullanıcı'})${
          expires === null ? '' : ` · oturum en geç ${formatDayMonth(expires)} ${formatClock(expires)}`
        }`
      : session.via === 'tailscale'
        ? 'Tailscale ile tanındın; bu yolda çıkış yok'
        : 'Geliştirme girişi';
  return (
    <section className="section" aria-labelledby="diger-hesap">
      <SectionHeader id="diger-hesap" title="Hesap" />
      <div className="setting">
        <span className="setting__label">
          {session.human?.displayName ?? 'Sahip'}
          <span className="setting__hint">{hint}</span>
        </span>
      </div>
      {session.via === 'session' ? (
        <button type="button" className="more-row more-row--button" onClick={() => void auth.logout()}>
          <Icon name="log-out" size={18} />
          <span className="more-row__title">Çıkış yap</span>
        </button>
      ) : null}
    </section>
  );
}

function DigerBody() {
  const { snapshot } = usePanel();
  const toSend = snapshot ? outboxWaiting(snapshot) : 0;
  return (
    <>
      {(['Satış', 'Mağaza', 'Sistem'] as const).map((group) => (
        <section key={group} className="section" aria-labelledby={`diger-${group}`}>
          <SectionHeader id={`diger-${group}`} title={group} />
          {ORDER.filter((id) => SECTION_META[id].group === group).map((id) => (
            <Link
              key={id}
              to={`/${id}`}
              className="more-row"
              aria-label={id === 'gonderilecekler' && toSend > 0 ? `Gönderilecekler, ${toSend} bekliyor` : undefined}
            >
              <Icon name={SECTION_META[id].icon} size={18} />
              <span className="more-row__title">{SECTION_META[id].title}</span>
              {id === 'gonderilecekler' && toSend > 0 ? <span className="badge badge--solid">{toSend}</span> : null}
              <Icon name="chevron-right" size={18} className="ink-3" />
            </Link>
          ))}
          {group === 'Sistem' ? <SettingsRows /> : null}
        </section>
      ))}
      <section className="section" aria-labelledby="diger-gorunum">
        <SectionHeader id="diger-gorunum" title="Görünüm" />
        <ThemeChooser />
        <OfflineToggle />
      </section>
      <AccountSection />
    </>
  );
}

export function Diger() {
  const state = usePanel();
  const desktop = useIsDesktop();
  if (desktop) {
    return (
      <>
        <TopBar title="Diğer" status={<TopBarStatus />} />
        <main className="desk-main desk-main--narrow">
          <DigerBody />
        </main>
      </>
    );
  }
  return (
    <>
      <AppBar title="Diğer" subtitle={formatLongDate(state.now)} />
      <StatusArea />
      <main className="phone-main">
        <DigerBody />
      </main>
      <BottomTabBar active="diger" />
    </>
  );
}

function SectionContent({ section }: { section: SectionId }) {
  const state = usePanel();
  const store = useStore();
  const snapshot = state.snapshot;
  if (!snapshot) return <Loading />;
  if (section === 'satislar') return <SalesPage />;
  if (section === 'satis-haritasi') return <SalesMapPage />;
  if (section === 'ilanlar') return <ListingsPage now={state.now} />;
  if (section === 'urunler') return <ProductsPage />;
  if (section === 'musteriler') return <CustomersPage />;
  if (section === 'site') return <SitePage />;
  if (section === 'kanallar') return <ChannelsPage />;
  if (section === 'seslendirme') return <VoicePage now={state.now} />;
  if (section === 'site-sohbetleri') return <SiteChatsPage />;
  if (section === 'seo') return <SeoPage />;
  if (section === 'ozet') return <OzetPage />;
  if (section === 'potansiyel-musteriler') return <ProspectsPage />;
  if (section === 'tanitim-gonderimi') return <OutreachPage />;
  if (section === 'tanitim-takibi') return <OutreachLogPage />;
  if (section === 'kayitlar') return <LogPage now={state.now} />;
  if (section === 'bilgi-bankasi') return <KbPage now={state.now} />;
  if (section === 'sekreter') return <SekreterPage now={state.now} />;
  if (section === 'gecmis-cevaplar') return <ReplyExamplesPage />;
  if (section === 'gonderilecekler') return <OutboxPage />;
  if (section === 'yapilanlar') {
    return (
      <div className="done-list">
        {snapshot.done.map((item) => (
          <DoneRow key={item.id} item={item} now={state.now} onRevert={() => void store.revert(item.id)} />
        ))}
      </div>
    );
  }
  return null;
}

/** Pages with wide tables use the whole desktop width. */
const WIDE_SECTIONS: ReadonlySet<SectionId> = new Set<SectionId>([
  'potansiyel-musteriler',
  'tanitim-takibi',
  'site',
  'kanallar',
  'seslendirme',
  'seo',
  'ozet',
  'satis-haritasi',
]);

export function SectionScreen({ section }: { section: SectionId }) {
  const state = usePanel();
  const desktop = useIsDesktop();
  const meta = SECTION_META[section];
  const body = (
    <>
      <p className="placeholder-text">{meta.text}</p>
      <SectionContent section={section} />
    </>
  );
  if (desktop) {
    return (
      <>
        <TopBar title={meta.title} status={<TopBarStatus />} />
        <main className={WIDE_SECTIONS.has(section) ? 'desk-main desk-main--wide' : 'desk-main desk-main--narrow'}>
          {body}
        </main>
      </>
    );
  }
  return (
    <>
      <AppBar title={meta.title} subtitle={`${meta.group} · ${formatLongDate(state.now)}`} />
      <StatusArea />
      <main className="phone-main">
        <Link to="/diger" className="back-link">
          <Icon name="chevron-left" size={18} />
          Diğer
        </Link>
        {body}
      </main>
      <BottomTabBar active="diger" />
    </>
  );
}
